import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../App";
import { Button } from "./ui/button";
import Header from "./Header";
import Footer from "./Footer";
import {
  ShieldCheck, FileText, Sparkles, Lock, Archive, RefreshCw,
  CheckCircle2, AlertTriangle, Calendar, ArrowRight, Building2,
  Scale, Globe2, Zap
} from "lucide-react";

const COPY = {
  fr: {
    seoTitle: "Obligations documentaires 2026 — Facturation électronique, RGPD, archivage légal | Documatch",
    seoDesc: "Toutes les obligations documentaires 2026 expliquées : facturation électronique (PPF, e-invoicing), RGPD, archivage électronique à valeur probante, dématérialisation et transformation digitale. Préparez votre GED avant l'échéance.",
    eyebrow: "Documatch · Guide officiel 2026",
    title: "Obligations Documentaires 2026 : ce que toute entreprise doit anticiper",
    subtitle: "Facturation électronique, RGPD, archivage légal, dématérialisation, transformation digitale — un point complet pour moderniser votre gestion documentaire avant les échéances réglementaires.",
    ctaPrimary: "Démarrer l'analyse IA",
    ctaSecondary: "Comparer les solutions GED",
    stats: [
      { k: "1er sept. 2026", v: "Échéance facturation électronique B2B" },
      { k: "10 ans", v: "Durée d'archivage légal RGPD" },
      { k: "+60%", v: "Productivité après dématérialisation" }
    ],
    introTitle: "Pourquoi 2026 change tout pour votre gestion documentaire",
    introBody: "À partir de 2026, les entreprises européennes basculent dans une nouvelle ère réglementaire. La facturation électronique devient obligatoire, le RGPD impose des contrôles renforcés, l'archivage à valeur probante est exigé sur 10 ans minimum, et la dématérialisation est désormais un prérequis pour rester compétitif. Une GED moderne n'est plus une option : c'est une infrastructure critique.",
    pillars: [
      {
        icon: FileText,
        title: "Facturation électronique 2026-2027",
        body: "Calendrier officiel : émission obligatoire pour toutes les entreprises au plus tard le 1er septembre 2027 (échéance fixée par la France). La réforme impose le passage par une plateforme de dématérialisation partenaire (PDP) ou le portail public (PPF). Toute GED doit pouvoir générer, signer et transmettre des factures conformes (Factur-X, UBL, CII)."
      },
      {
        icon: Lock,
        title: "RGPD : conformité documentaire renforcée",
        body: "Le règlement européen impose la traçabilité, le droit à l'effacement, le chiffrement des données sensibles et la pseudonymisation des documents contenant des données personnelles. Votre GED doit gérer le cycle de vie complet : collecte, conservation limitée, archivage, suppression certifiée. Sanctions : jusqu'à 4 % du CA mondial."
      },
      {
        icon: Archive,
        title: "Archivage électronique à valeur probante",
        body: "La norme NF Z42-013 (et son équivalent ISO 14641-1) exige un système d'archivage électronique (SAE) garantissant intégrité, traçabilité, pérennité et restitution. Durée légale : 10 ans pour les documents comptables, 5 ans pour les contrats commerciaux. Une GED non conforme expose à des litiges fiscaux et juridiques."
      },
      {
        icon: RefreshCw,
        title: "Dématérialisation documentaire",
        body: "Au-delà des factures, la dématérialisation touche RH (contrats, bulletins de paie), juridique (contrats commerciaux), achats (bons de commande) et marketing (brochures). Les entreprises qui dématérialisent gagnent en moyenne 45 % de productivité documentaire et réduisent leurs coûts de 30 %."
      },
      {
        icon: Sparkles,
        title: "Transformation digitale globale",
        body: "La GED s'intègre désormais à l'ERP, au CRM, à la signature électronique et aux outils IA. Sans interopérabilité, vous accumulez les silos. Une plateforme moderne expose une API ouverte, supporte les workflows automatisés et propose des connecteurs natifs (Sage, SAP, Odoo, HubSpot, Salesforce, Holded, Bizneo, Dynamics 365)."
      },
      {
        icon: ShieldCheck,
        title: "Sécurité et continuité d'activité",
        body: "Chiffrement TLS 1.3 + AES-256, hébergement souverain (OVH, Outscale), redondance multi-zone, plan de continuité, audit SOC 2 / ISO 27001. Toute GED retenue en 2026 doit présenter des garanties de sécurité de niveau bancaire."
      }
    ],
    timelineTitle: "Calendrier réglementaire 2026-2027",
    timeline: [
      { date: "1er septembre 2026", label: "Démarrage progressif : grandes entreprises obligées d'émettre en e-invoicing" },
      { date: "1er janvier 2027", label: "Extension à toutes les ETI et PME — réception obligatoire dès cette date" },
      { date: "1er septembre 2027", label: "Émission obligatoire pour 100 % des entreprises B2B en France" },
      { date: "Continu", label: "RGPD, NF Z42-013, ISO 27001 : audits annuels recommandés" }
    ],
    risksTitle: "Les risques si vous attendez",
    risks: [
      "Sanctions fiscales : amendes par facture non conforme + redressement TVA",
      "Sanctions RGPD : jusqu'à 4 % du chiffre d'affaires mondial",
      "Litiges juridiques : preuve documentaire non recevable en cas de contentieux",
      "Perte de compétitivité : 60 % de productivité en moins vs. concurrents dématérialisés",
      "Risque réputationnel : clients et partenaires exigent l'e-invoicing"
    ],
    bridgeTitle: "Du diagnostic à la solution : comment Documatch vous aide",
    bridgeBody: "Identifier la bonne GED parmi 50+ solutions européennes prend des semaines. Documatch utilise l'IA pour analyser vos besoins (secteur, taille, intégrations, conformité, budget) et vous recommande en moins de 5 minutes les 3 solutions les plus adaptées. Indépendant, sans biais commercial.",
    bridgeFlow: ["Sensibilisation 2026", "Analyse IA Documatch", "Comparaison des solutions GED", "Décision éclairée"],
    finalTitle: "Prêt à anticiper 2026 ?",
    finalSubtitle: "Lancez gratuitement votre analyse IA et recevez 3 recommandations personnalisées en moins de 5 minutes.",
    sectionRelated: "Ressources liées",
    related: [
      { title: "Pourquoi 80 % des comparatifs GED sont biaisés (et comment choisir la bonne solution)", href: "/blog/pourquoi-comparatifs-ged-biaises" },
      { title: "Les 10 ERP les plus utilisés en Europe en 2026", href: "/blog/erp-integration-ged-2026" },
      { title: "Facturation électronique 2026 : impact sur votre GED", href: "/blog/facturation-electronique-obligatoire-2026-ged" }
    ]
  },
  es: {
    seoTitle: "Obligaciones Documentales 2026 en España — Ley Crea y Crece, VeriFactu, factura electrónica, RGPD | Documatch",
    seoDesc: "Guía oficial 2026 para empresas españolas: Ley Crea y Crece, VeriFactu, factura electrónica B2B, AEAT, RGPD, conservación documental. Prepara tu DMS antes de las fechas límite de la AEAT.",
    eyebrow: "Documatch · Guía oficial España 2026",
    title: "Obligaciones Documentales 2026: lo que toda empresa española debe anticipar",
    subtitle: "Ley Crea y Crece, VeriFactu, factura electrónica B2B, AEAT, RGPD, conservación documental — un punto completo para modernizar tu gestión documental antes de las fechas reglamentarias que impone la administración española.",
    ctaPrimary: "Iniciar análisis IA",
    ctaSecondary: "Comparar soluciones GED",
    stats: [
      { k: "1 julio 2026", v: "VeriFactu obligatorio (Sociedades)" },
      { k: "Ley 18/2022", v: "Ley Crea y Crece (factura electrónica B2B)" },
      { k: "6 años", v: "Conservación documental Código de Comercio" }
    ],
    introTitle: "Por qué 2026 lo cambia todo en tu gestión documental",
    introBody: "A partir de 2026, las empresas españolas entran en una nueva era reglamentaria. La factura electrónica B2B se vuelve obligatoria según la Ley Crea y Crece, el sistema VeriFactu de la AEAT impone controles reforzados sobre los sistemas de facturación, el RGPD sigue vigente con sanciones agravadas y la conservación documental exige garantías de integridad. Una GED moderna ya no es una opción: es infraestructura crítica para operar en España.",
    pillars: [
      { icon: FileText, title: "Ley Crea y Crece — factura electrónica B2B", body: "La Ley 18/2022 (Ley Crea y Crece) impone la facturación electrónica entre empresarios y profesionales en España. Su despliegue progresivo obliga a todas las empresas a emitir y recibir facturas electrónicas estructuradas (Facturae, UBL). Tu GED debe generar, firmar, enviar y archivar facturas conformes con la normativa española, integrándose con la AEAT y los sistemas de intercambio autorizados." },
      { icon: ShieldCheck, title: "VeriFactu (Reglamento RD 1007/2023)", body: "El sistema VeriFactu de la AEAT entra en vigor progresivamente para empresas en España. Los Sistemas Informáticos de Facturación (SIF) deben garantizar la integridad, trazabilidad e inalterabilidad de las facturas emitidas, con envío en tiempo real a la Agencia Tributaria. Tu solución de facturación y GED debe estar certificada VeriFactu para evitar sanciones de hasta 50.000 € por sistema no conforme." },
      { icon: Lock, title: "RGPD y LOPDGDD — cumplimiento reforzado", body: "El Reglamento Europeo (UE) 2016/679 y la Ley Orgánica 3/2018 (LOPDGDD) imponen trazabilidad, derecho al olvido, cifrado de datos sensibles y seudonimización de documentos que contengan datos personales. Tu GED debe gestionar el ciclo de vida completo: recogida legítima, conservación limitada, archivado seguro, supresión certificada. Sanciones de la AEPD: hasta el 4 % de la facturación anual mundial o 20 M€." },
      { icon: Archive, title: "Conservación documental — normativa española", body: "El Código de Comercio (art. 30) obliga a conservar libros y documentos contables durante 6 años. La Ley General Tributaria fija 4 años (o más según prescripción). Los contratos comerciales relevantes deben conservarse hasta 5 años tras su vencimiento. La norma ENI (Esquema Nacional de Interoperabilidad) y las políticas de firma electrónica (eIDAS + LSSICE) exigen un Sistema de Archivo Electrónico (SAE) con valor probatorio." },
      { icon: RefreshCw, title: "Digitalización certificada AEAT", body: "La Orden EHA/962/2007 (actualizada) permite la digitalización certificada de facturas en papel para prescindir del original físico. Requiere software homologado por la AEAT, garantías de integridad e inalterabilidad, y flujos de captura documentados. Las empresas que digitalizan reducen espacio de archivo un 90 % y ganan hasta un 60 % de productividad documental." },
      { icon: Sparkles, title: "Transformación digital y Kit Digital", body: "El Kit Digital (Programa Next Generation UE gestionado por Red.es) financia hasta 12.000 € en soluciones digitales para pymes españolas, incluyendo GED, factura electrónica y ciberseguridad. La convocatoria 2026 mantiene el foco en digitalización obligatoria. Tu GED debe ser compatible Kit Digital y ofrecer conectores nativos con ERPs españoles (Sage, Holded, A3, Wolters Kluwer, Contasol)." }
    ],
    timelineTitle: "Calendario reglamentario España 2026-2027",
    timeline: [
      { date: "1 julio 2026", label: "VeriFactu obligatorio para contribuyentes del Impuesto sobre Sociedades" },
      { date: "1 enero 2027", label: "VeriFactu obligatorio para autónomos y resto de obligados" },
      { date: "Vigente desde 2022", label: "Ley Crea y Crece — obligación progresiva de factura electrónica B2B (desarrollo reglamentario en curso)" },
      { date: "Continuo", label: "RGPD, LOPDGDD, ENI y conservación documental (Código de Comercio, LGT) — auditorías AEPD periódicas" }
    ],
    risksTitle: "Los riesgos si esperas",
    risks: [
      "Sanciones AEAT: hasta 50.000 € por SIF no conforme con VeriFactu",
      "Sanciones AEPD (RGPD): hasta el 4 % de la facturación anual mundial o 20 M€",
      "Multas Ley Crea y Crece: 10.000 € por incumplimiento de la obligación de facturación electrónica",
      "Litigios judiciales: prueba documental no admisible si no cumple ENI / eIDAS",
      "Pérdida de competitividad: 60 % menos de productividad frente a competidores digitalizados",
      "Riesgo reputacional: clientes y grandes cuentas exigen ya factura electrónica y trazabilidad"
    ],
    bridgeTitle: "Del diagnóstico a la solución: cómo te ayuda Documatch",
    bridgeBody: "Identificar el GED adecuado entre más de 50 soluciones presentes en España lleva semanas. Documatch utiliza IA para analizar tus necesidades (sector, tamaño, ERP actual, cumplimiento AEAT/AEPD, presupuesto) y te recomienda en menos de 5 minutos las 3 soluciones más adaptadas al mercado español. Independiente, sin sesgo comercial.",
    bridgeFlow: ["Sensibilización 2026", "Análisis IA Documatch", "Comparativa soluciones GED", "Decisión informada"],
    finalTitle: "¿Listo para anticipar 2026?",
    finalSubtitle: "Inicia gratis tu análisis IA y recibe 3 recomendaciones personalizadas para el mercado español en menos de 5 minutos.",
    sectionRelated: "Recursos relacionados",
    related: [
      { title: "Por qué el 80 % de los comparativos GED son sesgados (y cómo elegir la solución correcta)", href: "/blog/pourquoi-comparatifs-ged-biaises" },
      { title: "Los 10 ERP más utilizados en España en 2026", href: "/blog/erp-integration-ged-2026" },
      { title: "Factura electrónica B2B en España: impacto en tu GED", href: "/blog/facturation-electronique-obligatoire-2026-ged" }
    ]
  },
  en: {
    seoTitle: "2026 EU Document Obligations — ViDA, Peppol, eInvoicing, GDPR | Documatch",
    seoDesc: "Official 2026 guide for European businesses: ViDA (VAT in the Digital Age), Peppol network, eInvoicing (Germany, Netherlands, Belgium), GDPR, European electronic archiving. Prepare your DMS before EU deadlines.",
    eyebrow: "Documatch · 2026 EU official guide",
    title: "2026 EU Document Obligations: what every European business must anticipate",
    subtitle: "ViDA, Peppol, eInvoicing (Germany, Netherlands, Belgium), GDPR, European electronic archiving — a complete overview to modernize your document management before EU regulatory deadlines.",
    ctaPrimary: "Start AI analysis",
    ctaSecondary: "Compare DMS solutions",
    stats: [
      { k: "Jan. 1, 2027", v: "B2B e-invoicing mandatory in Germany (Wachstumschancengesetz)" },
      { k: "2035", v: "EU ViDA full rollout (real-time reporting)" },
      { k: "Peppol", v: "Standard EU e-invoicing network" }
    ],
    introTitle: "Why 2026 changes everything for European document management",
    introBody: "From 2026, European businesses enter a new regulatory era. The EU 'VAT in the Digital Age' (ViDA) directive drives mandatory e-invoicing and real-time reporting. Germany's Wachstumschancengesetz mandates B2B e-invoicing from 2025-2028, the Netherlands and Belgium expand Peppol adoption, and GDPR enforcement intensifies across all Member States. A modern DMS is no longer optional: it is critical EU compliance infrastructure.",
    pillars: [
      { icon: FileText, title: "ViDA — VAT in the Digital Age", body: "The EU 'VAT in the Digital Age' package (Council adopted 11 March 2025) modernizes VAT rules across the 27 Member States. E-invoicing and digital reporting requirements (DRR) become the default. Cross-border B2B transactions require structured e-invoices and real-time VAT reporting by 2030-2035. Your DMS must comply with EN 16931 (European e-invoicing standard) and support UBL 2.1 / CII / Factur-X formats." },
      { icon: Globe2, title: "Peppol network — pan-European standard", body: "Peppol (Pan-European Public Procurement Online) is the de facto standard for B2B and B2G electronic exchange in Europe. Belgium mandates Peppol from 1 January 2026, the Netherlands and Germany rely on it heavily. Your DMS must connect to a certified Peppol Access Point to send and receive compliant invoices, orders and dispatch advices across the EU." },
      { icon: Building2, title: "Germany — Wachstumschancengesetz", body: "The German Growth Opportunities Act mandates B2B e-invoicing progressively: reception mandatory from 1 January 2025, issuance from 1 January 2027 for companies >€800k turnover, all companies by 1 January 2028. Format required: XRechnung or ZUGFeRD 2.x (EN 16931 compliant). Your DMS must integrate with the German Bundesportal and support archiving under GoBD rules (10 years retention)." },
      { icon: Scale, title: "Netherlands & Belgium — Peppol mandate", body: "The Netherlands has driven Peppol adoption since 2019 for B2G (Digipoort). Belgium's law of 6 February 2024 mandates B2B e-invoicing from 1 January 2026 via Peppol BIS 3.0. Compliance requires certified access, structured formats and 7-year retention. Your DMS must be Peppol-ready with native connectors to Dutch and Belgian authorities." },
      { icon: Lock, title: "GDPR — pan-European enforcement", body: "The General Data Protection Regulation (EU) 2016/679 remains the strongest data privacy law worldwide. Enforcement intensifies through the European Data Protection Board (EDPB) and national DPAs. Fines up to 4% of global turnover or €20M. Your DMS must handle full lifecycle: lawful collection, purpose limitation, encrypted storage (AES-256 + TLS 1.3), audit trails, right to erasure, cross-border transfer controls." },
      { icon: Archive, title: "Electronic archiving — EU sovereignty", body: "eIDAS 2.0 regulation (from 2024) reinforces trust services across the EU: qualified electronic signatures, seals, timestamps and electronic archiving. Long-term preservation (LTA) requires ETSI EN 319 122 compliance. National extensions: GoBD (Germany, 10 years), Belgian tax code (7 years), Dutch BW7 (7 years). Your DMS must provide qualified preservation and sovereign EU hosting (Frankfurt, Amsterdam, Brussels)." }
    ],
    timelineTitle: "EU regulatory timeline 2026-2028",
    timeline: [
      { date: "January 1, 2026", label: "Belgium — mandatory B2B e-invoicing via Peppol BIS 3.0" },
      { date: "January 1, 2027", label: "Germany — mandatory e-invoicing issuance for companies with turnover > €800k" },
      { date: "January 1, 2028", label: "Germany — mandatory e-invoicing for all B2B transactions" },
      { date: "2028-2030", label: "ViDA — Digital Reporting Requirements (DRR) rolled out across EU Member States" },
      { date: "2030-2035", label: "ViDA — full real-time reporting for cross-border B2B intra-EU transactions" }
    ],
    risksTitle: "The risks of waiting",
    risks: [
      "Tax penalties: fines per non-compliant invoice + VAT reassessment across EU Member States",
      "GDPR penalties: up to 4% of global turnover or €20M (EDPB enforcement)",
      "Peppol non-compliance: loss of B2G contracts in NL/BE, blocked B2B in Germany from 2027",
      "Legal disputes: document evidence not admissible under eIDAS 2.0 without qualified preservation",
      "Loss of competitiveness: 60% less productivity vs. digitally-native competitors",
      "Cross-border risk: inability to trade with Germany/NL/BE customers requiring Peppol"
    ],
    bridgeTitle: "From diagnosis to solution: how Documatch helps",
    bridgeBody: "Identifying the right DMS among 50+ European solutions takes weeks. Documatch uses AI to analyze your needs (sector, size, EU market coverage, Peppol readiness, GDPR compliance, budget) and recommends in under 5 minutes the 3 most relevant solutions for the European market. Independent, with no commercial bias.",
    bridgeFlow: ["2026 awareness", "Documatch AI analysis", "DMS comparison", "Informed decision"],
    finalTitle: "Ready to anticipate 2026?",
    finalSubtitle: "Start your AI analysis for free and receive 3 personalized recommendations tailored to the European market in under 5 minutes.",
    sectionRelated: "Related resources",
    related: [
      { title: "Why 80% of DMS comparisons are biased (and how to choose the right solution)", href: "/blog/pourquoi-comparatifs-ged-biaises" },
      { title: "The 10 most used ERPs in Europe in 2026", href: "/blog/erp-integration-ged-2026" },
      { title: "EU e-invoicing 2026: impact on your DMS", href: "/blog/facturation-electronique-obligatoire-2026-ged" }
    ]
  }
};

const HERO_IMG = "https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/1r0ql42u_Facturation%20%C3%A9lectronique%202026%20Facturaci%C3%B3n%20electr%C3%B3nica%202026%20Electronic%20invoicing%202026.png";

export default function ObligationsLanding() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const c = COPY[language] || COPY.fr;

  // SEO + JSON-LD
  useEffect(() => {
    const prevTitle = document.title;
    document.title = c.seoTitle;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    if (metaDesc) metaDesc.setAttribute("content", c.seoDesc);

    // JSON-LD WebPage + FAQPage
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "obligations-2026-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "name": c.title,
          "description": c.seoDesc,
          "url": "https://www.documatch.eu/obligations-2026",
          "inLanguage": language,
          "isPartOf": { "@type": "WebSite", "name": "Documatch", "url": "https://www.documatch.eu/" }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Documatch", "item": "https://www.documatch.eu/" },
            { "@type": "ListItem", "position": 2, "name": c.title, "item": "https://www.documatch.eu/obligations-2026" }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": c.pillars.map(p => ({
            "@type": "Question",
            "name": p.title,
            "acceptedAnswer": { "@type": "Answer", "text": p.body }
          }))
        }
      ]
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== null) metaDesc.setAttribute("content", prevDesc);
      const existing = document.getElementById("obligations-2026-jsonld");
      if (existing) existing.remove();
    };
  }, [language, c]);

  const startAnalysis = () => {
    navigate("/");
    setTimeout(() => {
      const el = document.getElementById("analyzer");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 150);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="obligations-2026-page">
      <Header
        onNavigate={(sec) => {
          navigate("/");
          setTimeout(() => document.getElementById(sec)?.scrollIntoView({ behavior: "smooth" }), 150);
        }}
        onShowAnalyzer={startAnalysis}
      />

      {/* Hero */}
      <section className="relative pt-24 pb-16 px-4 md:px-8 hero-gradient overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto relative z-10 grid md:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-wider mb-5">
              <Calendar size={14} className="text-blue-300" />{c.eyebrow}
            </span>
            <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5" data-testid="obligations-title">
              {c.title}
            </h1>
            <p className="text-white/80 text-base sm:text-lg mb-7 leading-relaxed">{c.subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={startAnalysis} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-7 py-6 rounded-xl shadow-lg shadow-blue-900/40" data-testid="obligations-cta-primary">
                <Zap size={18} className="mr-2" />{c.ctaPrimary}
              </Button>
              <Button onClick={() => { navigate("/"); setTimeout(() => document.getElementById("ged")?.scrollIntoView({ behavior: "smooth" }), 150); }} variant="outline" size="lg" className="bg-white/10 hover:bg-white/20 text-white border-white/30 font-semibold px-7 py-6 rounded-xl backdrop-blur-md" data-testid="obligations-cta-secondary">
                {c.ctaSecondary}
              </Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="hidden md:block">
            <img src={HERO_IMG} alt="Obligations documentaires 2026" className="w-full rounded-2xl shadow-2xl" />
          </motion.div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="py-10 px-4 bg-slate-900">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          {c.stats.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-slate-800 rounded-xl p-5 border border-slate-700">
              <p className="text-2xl font-bold text-white mb-1">{s.k}</p>
              <p className="text-sm text-slate-400">{s.v}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-5" data-testid="obligations-intro-title">{c.introTitle}</h2>
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed">{c.introBody}</p>
        </div>
      </section>

      {/* 6 Pillars */}
      <section className="py-14 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="obligations-pillars">
            {c.pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.article key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
                    <Icon size={22} className="text-blue-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{p.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center" data-testid="obligations-timeline-title">{c.timelineTitle}</h2>
          <ol className="relative border-l-2 border-blue-200 ml-3 space-y-7">
            {c.timeline.map((t, i) => (
              <li key={i} className="ml-6 relative">
                <span className="absolute -left-[34px] top-0 w-6 h-6 rounded-full bg-blue-600 border-4 border-white flex items-center justify-center text-white text-xs font-bold">{i + 1}</span>
                <p className="font-bold text-slate-900 text-sm">{t.date}</p>
                <p className="text-slate-600 text-sm mt-1">{t.label}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Risks */}
      <section className="py-14 px-4 bg-red-50/50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6 justify-center">
            <AlertTriangle size={28} className="text-red-600" />
            <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900">{c.risksTitle}</h2>
          </div>
          <ul className="space-y-3 max-w-2xl mx-auto">
            {c.risks.map((r, i) => (
              <li key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-red-100">
                <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">{r}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Bridge to AI Analysis */}
      <section className="py-14 px-4 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold mb-5">{c.bridgeTitle}</h2>
          <p className="text-white/85 text-base sm:text-lg leading-relaxed mb-7 max-w-3xl mx-auto">{c.bridgeBody}</p>
          <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
            {c.bridgeFlow.map((step, i) => (
              <span key={i} className="inline-flex items-center gap-2">
                <span className="px-3 py-1.5 bg-white/15 backdrop-blur-md rounded-lg text-sm font-medium">{step}</span>
                {i < c.bridgeFlow.length - 1 && <ArrowRight size={14} className="text-white/60" />}
              </span>
            ))}
          </div>
          <Button onClick={startAnalysis} size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 py-6 rounded-xl shadow-xl" data-testid="obligations-cta-bridge">
            <Zap size={18} className="mr-2" />{c.ctaPrimary}
          </Button>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 px-4 bg-slate-50">
        <div className="max-w-3xl mx-auto text-center">
          <CheckCircle2 size={36} className="text-green-600 mx-auto mb-4" />
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-3">{c.finalTitle}</h2>
          <p className="text-slate-600 mb-6">{c.finalSubtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={startAnalysis} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-7 py-6 rounded-xl" data-testid="obligations-cta-final-primary">
              {c.ctaPrimary}
            </Button>
            <Button onClick={() => { navigate("/"); setTimeout(() => document.getElementById("ged")?.scrollIntoView({ behavior: "smooth" }), 150); }} variant="outline" size="lg" className="border-slate-300 text-slate-700 hover:bg-white px-7 py-6 rounded-xl" data-testid="obligations-cta-final-secondary">
              {c.ctaSecondary}
            </Button>
          </div>
        </div>
      </section>

      {/* Related resources */}
      <section className="py-12 px-4 bg-white border-t border-slate-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-bold text-slate-900 mb-5">{c.sectionRelated}</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {c.related.map((r, i) => (
              <a key={i} href={r.href} className="block bg-slate-50 hover:bg-slate-100 rounded-xl p-5 transition-colors group" data-testid={`obligations-related-${i}`}>
                <p className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{r.title}</p>
                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">{language === "fr" ? "Lire l'article" : language === "es" ? "Leer artículo" : "Read article"} <ArrowRight size={12} /></p>
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer onLegalClick={() => {}} />
    </div>
  );
}
